import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { getInitialAplusData } from '../../helper/getInitialAplusData';
import type { AplusControlledSelection } from '../../Layouts/types';
import type { APlusItems } from '../../types';

const countDebounceMs = 1500;
const saveErrorVisibleMs = 5000;

/** the part of urql's `OperationResult` this hook needs */
type AplusMutationResult = { error?: unknown } | undefined;

/** what the customer asked for and the basket does not carry yet */
type Wanted = { selected?: boolean; count?: number };

type UseAplusSelectionProps = {
  /** the services as the basket currently has them */
  items: APlusItems;
  /** flips whether a single service is selected */
  toggleService: (uuid: string) => Promise<AplusMutationResult>;
  /** sends a new quantity for a single service */
  setServiceCount: (uuid: string, count: number) => Promise<AplusMutationResult>;
};

/**
 * Keeps the selection of an `AplusAccordion` and the basket in step.
 *
 * The basket is the only state the display falls back to. On top of it the hook holds what the
 * customer asked for and has not been confirmed yet, per service, and one worker per service sends
 * whatever it takes to get there. A change the backend does not take is dropped for that service
 * alone, which is all it takes for the display and the basket to agree again.
 */
export const useAplusSelection = ({
  items,
  toggleService,
  setServiceCount,
}: UseAplusSelectionProps) => {
  const [hasSaveError, setHasSaveError] = useState(false);
  const [wanted, setWanted] = useState<Map<string, Wanted>>(() => new Map());

  const confirmed = useMemo(() => getInitialAplusData(items), [items]);

  const confirmedRef = useRef(confirmed);
  useLayoutEffect(() => {
    confirmedRef.current = confirmed;
  }, [confirmed]);
  /** mirrors `wanted` for the workers, which read it between renders */
  const wantedRef = useRef(wanted);
  const sentRef = useRef(new Map<string, Wanted>());
  const workingRef = useRef(new Set<string>());
  const countTimeoutsRef = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  const errorTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const setWantedNow = (next: Map<string, Wanted>) => {
    wantedRef.current = next;
    setWanted(next);
  };

  const showError = () => {
    setHasSaveError(true);
    if (errorTimeoutRef.current) clearTimeout(errorTimeoutRef.current);
    errorTimeoutRef.current = setTimeout(() => setHasSaveError(false), saveErrorVisibleMs);
  };

  /** the backend did not take the change, so this service goes back to what the basket says */
  const forget = (uuid: string) => {
    const pendingCount = countTimeoutsRef.current.get(uuid);
    if (pendingCount) clearTimeout(pendingCount);
    countTimeoutsRef.current.delete(uuid);
    sentRef.current.delete(uuid);

    const next = new Map(wantedRef.current);
    next.delete(uuid);
    setWantedNow(next);

    showError();
  };

  /** the single request that brings this service closer to what the customer asked for */
  const nextStep = (uuid: string) => {
    const want = wantedRef.current.get(uuid);
    const server = confirmedRef.current.get(uuid);
    if (!want || !server) return undefined;

    // what the backend holds as far as this hook can tell: what the basket says, plus what was
    // asked for and has not come back yet. Asking for the same thing twice would toggle it back.
    const sent = sentRef.current.get(uuid);
    const assumedSelected = sent?.selected ?? server.selected;
    const assumedCount = sent?.count ?? server.value;

    if (want.selected !== undefined && want.selected !== assumedSelected) {
      return { sending: { selected: want.selected }, send: () => toggleService(uuid) };
    }

    // the quantity waits for its debounce, every service has its own
    if (
      want.count !== undefined &&
      want.count !== assumedCount &&
      !countTimeoutsRef.current.has(uuid)
    ) {
      const { count } = want;
      return { sending: { count }, send: () => setServiceCount(uuid, count) };
    }

    return undefined;
  };

  /**
   * One worker per service. It keeps stepping until the service has nothing left to send, so a
   * change made while a request was open is picked up when that request comes back, and a slow
   * service never holds up another one.
   */
  const work = (uuid: string) => {
    if (workingRef.current.has(uuid)) return;
    workingRef.current.add(uuid);

    const drain = async () => {
      try {
        for (let step = nextStep(uuid); step; step = nextStep(uuid)) {
          sentRef.current.set(uuid, { ...sentRef.current.get(uuid), ...step.sending });
          // eslint-disable-next-line no-await-in-loop
          const result = await step.send();
          // urql resolves a failed mutation with `error` set instead of rejecting
          if (result?.error) {
            forget(uuid);
            break;
          }
        }
      } catch {
        forget(uuid);
      } finally {
        workingRef.current.delete(uuid);
      }
    };

    drain();
  };

  const onItemChange = (uuid: string) => {
    const server = confirmedRef.current.get(uuid);
    if (!server) return;

    const want = wantedRef.current.get(uuid);
    const next = new Map(wantedRef.current);
    next.set(uuid, { ...want, selected: !(want?.selected ?? server.selected) });
    setWantedNow(next);

    work(uuid);
  };

  const onCountChange = (uuid: string, count: number) => {
    if (!confirmedRef.current.has(uuid)) return;

    const next = new Map(wantedRef.current);
    next.set(uuid, { ...wantedRef.current.get(uuid), count });
    setWantedNow(next);

    const pendingCount = countTimeoutsRef.current.get(uuid);
    if (pendingCount) clearTimeout(pendingCount);
    countTimeoutsRef.current.set(
      uuid,
      setTimeout(() => {
        countTimeoutsRef.current.delete(uuid);
        work(uuid);
      }, countDebounceMs),
    );
  };

  // once the basket carries what was asked for, there is nothing left to remember
  useEffect(() => {
    sentRef.current.forEach((sent, uuid) => {
      const server = confirmed.get(uuid);
      if (!server) return;

      const rest: Wanted = { ...sent };
      if (rest.selected === server.selected) delete rest.selected;
      if (rest.count === server.value) delete rest.count;

      if (rest.selected === undefined && rest.count === undefined) sentRef.current.delete(uuid);
      else sentRef.current.set(uuid, rest);
    });

    const next = new Map(wantedRef.current);
    let changed = false;

    next.forEach((want, uuid) => {
      const server = confirmed.get(uuid);
      if (!server) return;

      const rest: Wanted = { ...want };
      if (rest.selected === server.selected) delete rest.selected;
      if (rest.count === server.value) delete rest.count;
      if (rest.selected === want.selected && rest.count === want.count) return;

      changed = true;
      if (rest.selected === undefined && rest.count === undefined) next.delete(uuid);
      else next.set(uuid, rest);
    });

    if (changed) setWantedNow(next);
  }, [confirmed]);

  const data = useMemo(() => {
    if (wanted.size === 0) return confirmed;

    const merged = new Map(confirmed);
    wanted.forEach((want, uuid) => {
      const entry = merged.get(uuid);
      if (!entry) return;
      merged.set(uuid, {
        ...entry,
        selected: want.selected ?? entry.selected,
        value: want.count ?? entry.value,
      });
    });

    return merged;
  }, [confirmed, wanted]);

  useEffect(() => {
    const countTimeouts = countTimeoutsRef.current;
    return () => {
      if (errorTimeoutRef.current) clearTimeout(errorTimeoutRef.current);
      countTimeouts.forEach((timeout) => clearTimeout(timeout));
      countTimeouts.clear();
    };
  }, []);

  const controlled: AplusControlledSelection = { data, onItemChange, onCountChange };

  return { controlled, hasSaveError };
};
